import React, { useMemo } from "react";
import { compact, find, map } from "lodash";
import StackedBarChart from "@charty/stacked-bar-chart";
import StackedBarChartTooltip from "@charty/tooltip/chartjs-simple-serie";
import { Color } from "@glossy";
import { getMetricLabel } from "@linkfluence/i18n";
import { AbstractDate } from "@linkfluence/models/date";
import { Metric } from "@linkfluence/models/metric";
import { predefinedSort } from "@tootools/sort";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { DatavizStackedBarChart } from "@radarly/models/dataviz";
import { FullFacet } from "@radarly/models/facets";
import { SearchFragmentDateTerm } from "@radarly/models/search";
import { useRangeWithTimeUnit, useTimezoneFromFragment } from "../../data";
import Empty from "../../empty";
import Tooltip from "../../tooltip/metric";
import { computeBucketDataByTerm } from "../computation";

interface SharedWidgetsFieldStackedBarChartInnerProps {
  loading: boolean;
  data: SearchFragmentDateTerm["data"];
  fragment: SearchFragmentDateTerm;
  dataviz: DatavizStackedBarChart;
  facet: FullFacet;
  className?: string;
  onClickLegend?: (uid: string | number) => void;
  onClickElement?: (
    uid: string | number,
    range: { from: Date; to: Date }
  ) => void;
  disableAnimation?: boolean;
  nowDate?: AbstractDate;
  isFullscreen?: boolean;
}

const getFacetTermValues = (facet: FullFacet, term: string) =>
  find(facet.values, ({ id }) => id === term || id === parseInt(term));

const dataByFacet = (
  data: SearchFragmentDateTerm["data"],
  metric: Metric,
  facet: FullFacet
) => {
  const dataByTerm = computeBucketDataByTerm(data, facet.id, metric);

  //use predefined sort in facet as 'default' sort
  const sortOrder = map(facet.values, "id");
  return predefinedSort(
    compact(
      map(dataByTerm, (values, term) => {
        const value = getFacetTermValues(facet, term);
        if (value) {
          return {
            id: term,
            label: value.label,
            color: value.color as Color,
            data: values,
          };
        }
        return null;
      })
    ),
    "id",
    sortOrder
  );
};

export default function SharedWidgetsFieldStackedBarChartInner({
  loading,
  data,
  fragment,
  dataviz,
  facet,
  className,
  onClickElement,
  onClickLegend,
  disableAnimation,
  nowDate,
  isFullscreen,
}: SharedWidgetsFieldStackedBarChartInnerProps) {
  const locale = useCurrentUserLocale();
  const range = useRangeWithTimeUnit(fragment, loading, nowDate);
  const metric = fragment.request.aggs.date.metrics[0];
  const series = useMemo(
    () => dataByFacet(data, metric, facet),
    [data, facet, metric]
  );
  const timezone = useTimezoneFromFragment(fragment);
  return (
    <Empty empty={!series.length}>
      <StackedBarChart
        chartTooltip={(dataSeries) => (
          <StackedBarChartTooltip
            series={dataSeries}
            locale={locale}
            metricLabel={getMetricLabel(locale, metric)}
            isNormalized={dataviz.normalized}
            timeUnit={range.interval}
          />
        )}
        locale={locale}
        series={series}
        timezone={timezone}
        range={range}
        className={className}
        onClickBar={onClickElement}
        onClickLegend={onClickLegend}
        sort={
          dataviz.sort === "alpha"
            ? "alpha"
            : dataviz.sort === "default"
            ? "default"
            : "mainValue"
        }
        tooltip={(serie, total) => (
          <Tooltip
            range={range}
            count={serie.count}
            color={serie.color}
            label={serie.label}
            metric={metric}
            locale={locale}
            isNormalized={dataviz.normalized}
            total={total}
          />
        )}
        isNormalized={dataviz.normalized}
        scale={dataviz.scale || "linear"}
        disableAnimation={disableAnimation}
        isFullscreen={isFullscreen}
        metricLabel={getMetricLabel(locale, metric)}
        displayVolumes={dataviz.displayVolumes}
      />
    </Empty>
  );
}
